import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';

interface ChartDataPoint {
  timestamp: string;
  price: number;
  volume?: number;
  high?: number;
  low?: number;
  open?: number;
  close?: number;
}

interface TradingChartProps {
  data: ChartDataPoint[];
  symbol: string;
}

const TradingChart: React.FC<TradingChartProps> = ({ data, symbol }) => {
  const [chartType, setChartType] = useState<'line' | 'area'>('area');
  const [timeframe, setTimeframe] = useState<'1h' | '4h' | '1d' | '1w'>('1h');
  
  // Función para formatear las fechas en el eje X
  const formatXAxis = (tickItem: string) => {
    const date = new Date(tickItem);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  // Formatear datos para el gráfico
  const formatData = (rawData: ChartDataPoint[]) => {
    if (!rawData || rawData.length === 0) return [];
    
    return rawData.map(item => ({
      ...item,
      // Asegúrate de que las fechas sean objetos Date para Recharts
      time: new Date(item.timestamp).getTime(),
      // Formatear para vista más limpia
      formattedTime: formatXAxis(item.timestamp),
      // Valores redondeados para mejor visualización
      displayPrice: parseFloat(item.price.toFixed(2))
    }));
  };
  
  const chartData = formatData(data);
  
  // Calcular mínimo y máximo para los ejes Y
  const minPrice = Math.min(...chartData.map(item => item.price)) * 0.995; // 0.5% menos
  const maxPrice = Math.max(...chartData.map(item => item.price)) * 1.005; // 0.5% más
  
  // Personalizar el tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip bg-dark text-light p-2 rounded">
          <p className="mb-1">{new Date(label).toLocaleString()}</p>
          <p className="mb-0">
            <span className="text-info">Precio: </span>
            <span className="fw-bold">${payload[0].value.toFixed(2)}</span>
          </p>
        </div>
      );
    }
    return null;
  };
  
  return (
    <div className="trading-chart">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="btn-group btn-group-sm" role="group">
          <button 
            type="button" 
            className={`btn ${chartType === 'line' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setChartType('line')}
          >
            Línea
          </button>
          <button 
            type="button" 
            className={`btn ${chartType === 'area' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setChartType('area')}
          >
            Área
          </button>
        </div>
        
        <div className="btn-group btn-group-sm" role="group">
          <button 
            type="button" 
            className={`btn ${timeframe === '1h' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setTimeframe('1h')}
          >
            1H
          </button>
          <button 
            type="button" 
            className={`btn ${timeframe === '4h' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setTimeframe('4h')}
          >
            4H
          </button>
          <button 
            type="button" 
            className={`btn ${timeframe === '1d' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setTimeframe('1d')}
          >
            1D
          </button>
          <button 
            type="button" 
            className={`btn ${timeframe === '1w' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setTimeframe('1w')}
          >
            1W
          </button>
        </div>
      </div>
      
      <ResponsiveContainer width="100%" height={400}>
        {chartType === 'line' ? (
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="time" 
              tickFormatter={formatXAxis}
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              domain={[minPrice, maxPrice]}
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => `$${value.toFixed(1)}`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="price" 
              name={symbol} 
              stroke="#8884d8" 
              dot={false}
              activeDot={{ r: 6 }}
              strokeWidth={2}
            />
          </LineChart>
        ) : (
          <AreaChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="time" 
              tickFormatter={formatXAxis}
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              domain={[minPrice, maxPrice]}
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => `$${value.toFixed(1)}`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <defs>
              <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#8884d8" stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <Area 
              type="monotone" 
              dataKey="price" 
              name={symbol}
              stroke="#8884d8" 
              fillOpacity={1} 
              fill="url(#colorPrice)" 
            />
          </AreaChart>
        )}
      </ResponsiveContainer>
      
      {chartData.length > 0 && (
        <div className="d-flex justify-content-between mt-2 text-muted small">
          <div>Apertura: ${chartData[0].price.toFixed(2)}</div>
          <div>Actual: ${chartData[chartData.length - 1].price.toFixed(2)}</div>
          <div>
            Cambio: {(((chartData[chartData.length - 1].price - chartData[0].price) / chartData[0].price) * 100).toFixed(2)}%
          </div>
        </div>
      )}
    </div>
  );
};

export default TradingChart;
